import React from "react";
import { useNavigate } from "react-router-dom";
import { initialsOf, formatFollowUpDate } from "./utils";
import {
  useFollowupNotification,
  MIN_NOTIFY_LEAD_SEC,
} from "../../../context/FollowupNotificationContext";

const FollowupReport = ({
  followUps,
  followUpsLoading,
  selectedDay,
  selectedDayLabel,
  setSelectedDay,
  makeCall,
  hasWebRtc,
}) => {
  const navigate = useNavigate();
  // The countdown/auto-dial itself now runs globally (see
  // FollowupNotificationContext + FollowupCallToast) so it keeps working
  // when the agent navigates off this dashboard; this card just surfaces
  // the on/off + lead-time settings for that shared behavior.
  const { notifyEnabled, toggleNotify, notifyLeadSec, setNotifyLeadSeconds } =
    useFollowupNotification();

  const handleNotifyLeadSecChange = (e) => {
    const raw = parseInt(e.target.value, 10);
    setNotifyLeadSeconds(raw);
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>
          My Follow-ups
          {followUps.length > 0 && (
            <span className="followup-count-badge">{followUps.length} open</span>
          )}
        </h2>
        <div className="card-head-actions">
          <div
            className={`toggle-pill${notifyEnabled ? " on" : ""}`}
            onClick={toggleNotify}
            title={notifyEnabled ? "Turn off follow-up notifications" : "Turn on follow-up notifications"}
          >
            <span className="knob"></span>
            <span>{notifyEnabled ? "Notifications On" : "Notifications Off"}</span>
          </div>
          {notifyEnabled && (
            <label className="followup-lead-input" title="How many seconds before the follow-up time to show the notification">
              Notify
              <input
                type="number"
                min={MIN_NOTIFY_LEAD_SEC}
                value={notifyLeadSec}
                onChange={handleNotifyLeadSecChange}
              />
              s before
            </label>
          )}
          <button
            type="button"
            className="followup-viewall-btn"
            onClick={() => navigate("/agent/report/followup")}
          >
            View all →
          </button>
        </div>
      </div>
      {selectedDay && (
        <div className="followup-filter-banner">
          <span>
            Showing <strong>{followUps.length}</strong> follow-up(s) for{" "}
            <strong>{selectedDayLabel}</strong>
          </span>
          <button
            type="button"
            className="followup-clear-link"
            onClick={() => setSelectedDay(null)}
          >
            Clear ×
          </button>
        </div>
      )}
      <div className="followup-list">
        {followUpsLoading ? (
          <div className="empty-state">Loading follow-ups…</div>
        ) : followUps.length === 0 ? (
          <div className="empty-state">
            {selectedDay ? "No follow-ups scheduled for this date." : "No follow-ups to show."}
          </div>
        ) : (
          followUps.map((f, idx) => {
            const questions = f.feedback_questions || {};
            const labels = f.feedback_labels || {};
            const qKeys = Object.keys(questions);
            const dateInfo = formatFollowUpDate(f.follow_up_date);
            return (
              <div className="followup-row" key={`${f.customer_number}-${f.follow_up_date}-${idx}`}>
                <div className="followup-left">
                  <div className="call-icon" style={{ background: "var(--primary)" }}>
                    {initialsOf(f.customer_name)}
                  </div>
                  <div className="call-meta">
                    <div className="name">{f.customer_name}</div>
                    <div className="sub">{f.customer_number}</div>
                  </div>
                </div>
                <div className="followup-feedback">
                  <div className="followup-task">{f.campaign_name}</div>
                  <div className="followup-comment">{f.comment || ""}</div>
                  {qKeys.map((q) => (
                    <div key={q} className="followup-feedback-line">
                      <span className="q">{questions[q]}</span>
                      {labels[q] ? <span className="a"> — {labels[q]}</span> : null}
                    </div>
                  ))}
                </div>
                {dateInfo && (
                  <div className={`followup-date ${dateInfo.tone}`}>
                    {dateInfo.label}
                    {dateInfo.tone === "overdue" && (
                      <span className="followup-missed-badge">Missed</span>
                    )}
                  </div>
                )}
                {hasWebRtc && (
                  <button
                    type="button"
                    className="followup-call-btn"
                    onClick={() => makeCall(f.customer_number)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    Call
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FollowupReport;
